import { useEffect } from 'react';

/**
 * SEOSchema Component
 * Adds structured data (JSON-LD) for better search engine optimization
 * 
 * @param {string} type - Schema type: 'organization', 'breadcrumb', 'article', etc.
 * @param {object} data - Schema data object
 */
const SEOSchema = ({ type, data }) => {
  const schemaGenerators = {
    organization: (data) => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: data.name || "Spinnaker Algo Systems Limited",
      url: data.url || "https://www.spinnakeralgo.com",
      logo: data.logo || "https://www.spinnakeralgo.com/logo.png",
      description: data.description || "Advanced quantitative systems for financial markets",
      address: data.address || {
        "@type": "PostalAddress",
        streetAddress: "Capital Office, 124 City Road",
        addressLocality: "London",
        postalCode: "EC1V 2NX",
        addressCountry: "GB"
      },
      contactPoint: data.contactPoint || {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: "inquiries@spinnakeralgo.com"
      },
      sameAs: data.sameAs || [
        "https://www.linkedin.com/company/spinnaker-algo-ltd/"
      ]
    }),
    
    breadcrumb: (data) => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: (data.items || []).map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url
      }))
    }),
    
    article: (data) => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: data.headline,
      description: data.description,
      author: data.author || {
        "@type": "Organization",
        name: "Spinnaker Algo Systems Limited"
      },
      publisher: data.publisher || {
        "@type": "Organization",
        name: "Spinnaker Algo Systems Limited",
        logo: {
          "@type": "ImageObject",
          url: "https://www.spinnakeralgo.com/logo.png"
        }
      },
      datePublished: data.datePublished,
      dateModified: data.dateModified
    }),
  };

  const generator = schemaGenerators[type];
  const schema = generator ? generator(data) : null;

  useEffect(() => {
    if (!schema) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = `schema-${type}`;
    
    // Remove existing schema of same type
    const existing = document.getElementById(`schema-${type}`);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      const cleanup = document.getElementById(`schema-${type}`);
      if (cleanup) {
        cleanup.remove();
      }
    };
  }, [schema, type]);

  return null;
};

export default SEOSchema;
